"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleDashed } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2018",
  title: "The First Encounter",
  description: "A chance meeting at a small gallery opening in the city. Amidst the art and soft whispers, Jane and Liam found a connection that felt like coming home.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_first_meeting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_first_meeting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_first_meeting.png-wvc-sizes",
    alt: "Jane and Liam meeting for the first time"
  }
}, {
  year: "2020",
  title: "Building Our World",
  description: "Through quiet mornings and shared dreams, we began weaving our lives together. Every sunset watched and every challenge faced only made our bond stronger.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_travel_memories.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_travel_memories.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_travel_memories.png-wvc-sizes",
    alt: "Jane and Liam traveling together"
  }
}, {
  year: "2023",
  title: "The Promise",
  description: "Under a canopy of stars on a crisp autumn evening, Liam asked the question that would change everything. A simple 'Yes' began our journey to forever.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_engagement.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_engagement.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_engagement.png-wvc-sizes",
    alt: "The engagement moment"
  }
}, {
  year: "Today",
  title: "Our Path to I Do",
  description: "Now, we stand on the threshold of a new chapter. We are so honored to have you join us as we celebrate the love that has grown into our beautiful reality.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_wedding_prep.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_wedding_prep.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_wedding_prep.png-wvc-sizes",
    alt: "Jane and Liam today"
  }
}];
function TimelineItem({
  milestone,
  index
}: {
  milestone: (typeof milestones)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("relative flex items-center justify-between w-full mb-16 md:mb-24 last:mb-0 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      {}
      <div className="absolute left-8 md:left-1/2 w-12 h-12 rounded-lg bg-secondary border-4 border-background flex items-center justify-center -translate-x-1/2 z-10 shadow-md">
        <CircleDashed className="w-6 h-6 text-primary" />
      </div>

      {}
      <div className={cn("w-full flex", isEven ? "md:justify-start" : "md:justify-end")}>
        <div className={cn("w-full md:w-[45%] pl-24 md:pl-0", isEven ? "md:pr-16" : "md:pl-16")}>
          <Card className="overflow-hidden border-none shadow-lg shadow-shadow-sm rounded-lg bg-card/40 backdrop-blur-sm transition-transform duration-500 hover:-translate-y-1">
            <AspectRatio ratio={16 / 10}>
              <img src={milestone.image.src} data-wvc-srcset={milestone.image.srcset} data-wvc-sizes={milestone.image.sizes} alt={milestone.image.alt} className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" />
            </AspectRatio>
            <CardContent className="p-6 md:p-8">
              <span className="text-xs font-sans font-medium tracking-widest text-primary uppercase mb-3 block">
                {milestone.year}
              </span>
              <h3 className="text-2xl font-default font-normal italic mb-4 text-foreground tracking-tight">
                {milestone.title}
              </h3>
              <p className="text-foreground/80 leading-relaxed font-sans text-sm tracking-wide">
                {milestone.description}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}
export default function UnsereGeschichte() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="245893" id="our-path-to-i-do" className="relative py-24 md:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-accent/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-1000", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-6xl font-default font-light text-foreground mb-6 tracking-tighter">
            Our Path to I Do
          </h2>
          <p className="text-lg text-foreground/70 font-sans italic tracking-wide">
            A collection of moments that defined us, from our first meeting to the 
            beautiful promise we are about to make.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-4 bottom-4 w-0 border-l border-dashed border-primary/20 -translate-x-1/2 z-0" />

          {}
          <div className="relative z-10">
            {milestones.map((milestone, i) => <TimelineItem key={i} milestone={milestone} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}